import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import {
  Button as UIButton,
  Badge as UIBadge,
  Card,
  CardHeader,
  Skeleton as UISkeleton,
  buttonClass as uiButtonClass,
  type ButtonProps as UIButtonProps,
  type ButtonVariant,
} from "@clubghor/ui";
import { cn } from "~/lib/utils";

/* ============================================================================
   Admin primitives
   ----------------------------------------------------------------------------
   A thin adapter over @clubghor/ui. The styling now lives in the shared library
   (packages/ui/src/styles.css), so the admin and the storefront render the same
   buttons, fields and badges, with the admin running the compact density set on
   <html data-density="compact">.

   The admin's original prop names are kept (`variant="outline"`, `size="icon"`)
   so no screen had to change when the implementation moved.
   ========================================================================= */

type Variant = "primary" | "house" | "outline" | "ghost" | "danger";
type Size = "sm" | "md" | "lg" | "icon" | "icon-sm";

const VARIANT_MAP: Record<Variant, ButtonVariant> = {
  primary: "primary",
  house: "house",
  outline: "secondary",
  ghost: "ghost",
  danger: "danger",
};

function mapSize(size: Size): { size: "sm" | "md" | "lg"; iconOnly: boolean } {
  if (size === "icon") return { size: "md", iconOnly: true };
  if (size === "icon-sm") return { size: "sm", iconOnly: true };
  return { size, iconOnly: false };
}

export interface ButtonProps extends Omit<UIButtonProps, "variant" | "size" | "iconOnly"> {
  variant?: Variant;
  size?: Size;
}

/** The button's classes, so a real `<a>` (navigation, a file download) can match. */
export function buttonClass(options: { variant?: Variant; size?: Size; block?: boolean; className?: string } = {}) {
  const { variant = "outline", size = "md", block, className } = options;
  return uiButtonClass({ variant: VARIANT_MAP[variant], ...mapSize(size), block, className });
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "outline", size = "md", ...rest },
  ref,
) {
  return <UIButton ref={ref} variant={VARIANT_MAP[variant]} {...mapSize(size)} {...rest} />;
});

/* Form controls are the shared ones, unchanged in API. */
export { Input, Textarea, Field } from "@clubghor/ui";
export type { InputProps } from "@clubghor/ui";

/* ============================================================================
   Badges
   ========================================================================= */

type Tone = "neutral" | "success" | "warning" | "danger" | "info" | "primary";

export function Badge({
  tone = "neutral",
  children,
  className,
  dot = false,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
  dot?: boolean;
}) {
  return (
    <UIBadge tone={tone} dot={dot} className={className}>
      {children}
    </UIBadge>
  );
}

/* ============================================================================
   Surfaces
   ========================================================================= */

export function Surface({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <Card className={className} {...rest}>
      {children}
    </Card>
  );
}

export function SurfaceHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return <CardHeader title={title} description={description} actions={actions} className={className} />;
}

/* ============================================================================
   Loading, empty and error states
   ----------------------------------------------------------------------------
   Every list in this app renders all four states. The previous admin only ever
   rendered the success case, so a slow or empty response looked like a bug.
   ========================================================================= */

export function Skeleton({ className }: { className?: string }) {
  return <UISkeleton className={className} />;
}

/** Table skeleton shaped like the real rows, so the layout does not jump. */
export function TableSkeleton({ rows = 8, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="divide-y divide-[color-mix(in_oklab,var(--cg-line)_55%,transparent)]" aria-hidden="true">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 px-3 py-3.5">
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn("h-4", columnIndex === 0 ? "w-1/3" : "w-16", columnIndex === columns - 1 && "ml-auto")}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export { EmptyState, ErrorState, Kbd, Separator } from "@clubghor/ui";
